import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Tasks.css";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { getDeletedTasks, restoreTask } from "../api/tasksApi";
import { ADMIN_ROLES, hasAnyRole } from "../utils/roles";
import Pagination from "../components/Pagination";

const normalizeId = (id) => {
  if (!id) return "";
  if (typeof id === "object") {
    if (id.$oid) return String(id.$oid).trim();
    if (id._id) return normalizeId(id._id);
    if (id.id) return normalizeId(id.id);
  }
  return String(id).trim();
};

const formatTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString();
};

export default function DeletedTasks() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [deletedTasks, setDeletedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const isAdmin = hasAnyRole(currentUser, ADMIN_ROLES);

  const loadDeletedTasks = async (pageNum = 1) => {
    setLoading(true);
    try {
      const data = await getDeletedTasks(pageNum, 10);

      let list = [];
      let totalCount = 0;

      if (Array.isArray(data)) {
        list = data;
        totalCount = data.length;
      } else if (data?.data && Array.isArray(data.data)) {
        list = data.data;
        totalCount = data.total || data.data.length;
      } else if (data?.data?.data && Array.isArray(data.data.data)) {
        list = data.data.data;
        totalCount = data.data.meta?.total || data.data.data.length;
      } else if (data?.tasks && Array.isArray(data.tasks)) {
        list = data.tasks;
        totalCount = data.total || data.tasks.length;
      } else if (data?.deletedTasks && Array.isArray(data.deletedTasks)) {
        list = data.deletedTasks;
        totalCount = data.total || data.deletedTasks.length;
      } else if (data?.deleted && Array.isArray(data.deleted)) {
        list = data.deleted;
        totalCount = data.total || data.deleted.length;
      } else {
        console.warn("Unexpected response structure:", data);
      }

      setDeletedTasks(list);
      setTotal(totalCount);
      setPage(pageNum);
    } catch (err) {
      console.error("Failed to load deleted tasks:", err);
      toast.error(err?.message || "Failed to load deleted tasks");
      setDeletedTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id) => {
    try {
      await restoreTask(id);
      toast.success("Task restored successfully");
      setDeletedTasks((prev) => prev.filter((t) => normalizeId(t._id) !== normalizeId(id)));
      loadDeletedTasks(page);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to restore task");
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadDeletedTasks();
    }
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="tasks-page">
        <div className="tasks-loading">
          <p>Access denied</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <div className="header-left">
          <h2>Deleted Tasks</h2>
          <span className="total-badge">{total} deleted tasks</span>
        </div>
        <div className="header-right">
          <button className="create-btn" onClick={() => navigate("/tasks")}>Back to Tasks</button>
        </div>
      </div>

      {loading ? (
        <div className="tasks-loading">
          <p>Loading deleted tasks...</p>
        </div>
      ) : deletedTasks.length === 0 ? (
        <div className="tasks-loading">
          <p>No deleted tasks found</p>
        </div>
      ) : (
        <div className="tasks-board">
          <div className="task-column">
            <div className="column-header">
              <h3>Deleted Tasks</h3>
            </div>
            <div className="task-list">
              {deletedTasks.map((task) => (
                <div key={normalizeId(task._id)} className="task-card">
                  <div className="priority-stripe" style={{ background: "#ff6b6b" }} />

                  <div className="card-top">
                    <h3>{task.title || task.name || "Untitled Task"}</h3>
                    <div className="card-badges">
                      <span className="priority-badge" style={{ color: "#ff6b6b" }}>
                        DELETED
                      </span>
                    </div>
                  </div>

                  {task.description && <p className="task-desc">{task.description}</p>}

                  {task.assigneeUserName && (
                    <div className="task-assignee-row">
                      <span className="assignee-avatar-large">
                        {(task.assigneeUserName || "?")[0].toUpperCase()}
                      </span>
                      <span className="assignee-name">{task.assigneeUserName}</span>
                    </div>
                  )}

                  <div className="task-meta">
                    {task.position && <span className="meta-position">{task.position}</span>}
                    {task.dueDate && (
                      <span className="meta-deadline">
                        📅 {formatTime(task.dueDate)}
                      </span>
                    )}
                  </div>

                  <div className="card-footer">
                    <button className="edit-btn" onClick={() => handleRestore(task._id)}>
                      🔄 Restore
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Pagination
        currentPage={page}
        totalPages={Math.ceil(total / 10)}
        onChange={loadDeletedTasks}
      />
    </div>
  );
}
